import { useLocation, useNavigate } from 'react-router-dom';
import type { VaultItem } from '@heybesafe/shared';
import { effectiveWebIdentifiers, ensureUrlScheme } from '@heybesafe/shared';
import { HbsListRow } from './hbs/ListRow';
import { HbsSiteIcon } from './hbs/SiteIcon';
import { HbsCopyMenu } from './hbs/CopyMenu';
import { Icon } from './hbs/Icon';
import { domainFromUrl } from '../utils/domainFromUrl';
import { candidateFaviconUrls, registrableDomain } from '../utils/faviconUrl';

interface SafeItemRowProps {
  item: VaultItem;
  /** Highlights the row — used by the personal Safe list's route-based selection. */
  selected?: boolean;
}

/**
 * One saved-login row, shared by the personal Safe list, the collapsible
 * Family Safe sections, and the Family overview list — so all three look
 * identical. The View action routes to the personal or family detail page
 * depending on whether the item belongs to a family (`orgId`).
 */
export function SafeItemRow({ item, selected }: SafeItemRowProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Modern items store launch URLs in identifiers[]; legacy items use
  // the bare `url` field. Read identifiers first, fall back to `url`, then
  // tolerate a missing scheme so a saved `example.com` launches correctly.
  const allIdentifierUrls = effectiveWebIdentifiers(item);
  const launchHref = ensureUrlScheme(allIdentifierUrls[0] ?? item.url ?? '');
  const iconHost = domainFromUrl(launchHref || item.url, '');
  // Display domain (monogram letter + color) uses the registrable domain so a
  // saved `www.adidas.com` shows "A", not "W". Falls back to the item name
  // when there's no parseable host.
  const domain = iconHost ? registrableDomain(iconHost) : item.name;
  const faviconUrls = candidateFaviconUrls(item);
  const detailPath = item.orgId
    ? `/orgs/${item.orgId}/items/${item.id}`
    : `/safe/${item.id}`;

  function handleLaunch(): void {
    if (!launchHref) return;
    window.open(launchHref, '_blank', 'noopener,noreferrer');
  }

  function handleCopyUsername(): void {
    void navigator.clipboard.writeText(item.username ?? '');
  }

  function handleCopyPassword(): void {
    void navigator.clipboard.writeText(item.password ?? '');
  }

  const hasEmbeddedPasskeys =
    item.type === 'login' && !!item.passkeys && item.passkeys.length > 0;

  return (
    <HbsListRow
      id={`safe-item-row-${item.id}`}
      leading={
        <span className="relative inline-flex items-center justify-center">
          <HbsSiteIcon domain={domain} faviconUrls={faviconUrls} size={34} />
          {hasEmbeddedPasskeys && (
            <span
              title="Has passkey"
              aria-label="Has passkey"
              className="absolute -bottom-1 -right-1 text-hbs-primary leading-none"
            >
              <Icon name="key" size={11} />
            </span>
          )}
        </span>
      }
      title={item.name}
      supporting={item.sensitive ? undefined : (item.username ?? launchHref ?? '')}
      selected={selected}
      onClick={launchHref ? handleLaunch : undefined}
      overlay={launchHref ? { label: 'Launch Site', icon: <Icon name="launch" size={14} /> } : undefined}
      actions={
        <>
          <HbsCopyMenu
            idPrefix={`safe-item-copy-${item.id}`}
            onCopyUsername={handleCopyUsername}
            onCopyPassword={handleCopyPassword}
            hasUsername={!!item.username}
            hasPassword={!!item.password}
          />
          <button
            id={`safe-item-view-${item.id}`}
            type="button"
            aria-label="View"
            // Carry the current query string (e.g. the Safe list's `?q=` search)
            // onto the detail route so opening an item doesn't clear an active
            // search — the list stays filtered behind the detail panel.
            onClick={() => navigate({ pathname: detailPath, search: location.search })}
            className="p-1.5 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
          >
            <Icon name="view" size={14} />
          </button>
        </>
      }
    />
  );
}
